import { Schema } from "effect";

/**
 * Base interface for all domain events in the invoice system.
 * Domain events represent business-significant occurrences that have happened
 * in the past and are immutable facts about the system.
 * 
 * Following DDD principles:
 * - Events are immutable and represent facts that have occurred
 * - Events are named in past tense (e.g., InvoiceCreated, OrderProcessed)
 * - Events carry all necessary data for subscribers to react appropriately
 * - Events are published by aggregates when their state changes
 */
export interface DomainEvent {
  /** Unique identifier for the event type - used for routing and filtering */
  readonly type: string;
  
  /** Unique identifier for this specific event instance */
  readonly eventId: string;
  
  /** Timestamp when the event occurred */
  readonly occurredOn: Date;
  
  /** Version of the event schema for evolution compatibility */
  readonly version: number;
  
  /** The aggregate type that published this event */
  readonly aggregateType: string;
  
  /** The ID of the aggregate instance that published this event */
  readonly aggregateId: string;
  
  /** Optional correlation ID for tracing related events */
  readonly correlationId?: string;
  
  /** Optional causation ID - the event that caused this event */
  readonly causationId?: string;
}

/**
 * Base schema for domain events with common validation rules
 */
export const DomainEventSchema = Schema.Struct({
  type: Schema.String.pipe(Schema.nonEmptyString()),
  eventId: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      {
        message: () => "Event ID must be a valid UUID",
      }
    )
  ),
  occurredOn: Schema.DateFromSelf,
  version: Schema.Number.pipe(
    Schema.int(),
    Schema.greaterThanOrEqualTo(1, {
      message: () => "Event version must be a positive integer",
    })
  ),
  aggregateType: Schema.String.pipe(Schema.nonEmptyString()),
  aggregateId: Schema.String.pipe(Schema.nonEmptyString()),
  correlationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  causationId: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
});

/**
 * Type alias for the domain event schema type
 */
export type DomainEventType = Schema.Schema.Type<typeof DomainEventSchema>;
